import { forwardRef, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import useCommonStyles from '../../../../../commonStyles.js';
import InputConfig from '../../../../../Components/InputConfig/index.jsx';
import { UnitsContext } from '../../../../../Contexts/UnitsContext/index.jsx';
import { UserContext } from '../../../../../Contexts/UserContext/index.jsx';
import { toast } from 'react-toastify';

import {
	AB_SPLIT_TEST_CONSTANTS,
	CONFIG_KEYDATA_CONSTANTS,
	MULTI_SPLIT_TEST_CONSTANTS,
	SPLIT_LEVELS
} from '../../../../../core/constants.js';
import DropDownSingle from '../../../../../Components/DropDownSingle/index.jsx';
import CheckBoxInput from '../../../../../Components/CheckBoxInput/index.jsx';
import { getExperimentSplitInfoOfSection } from '../../UnitCreatorUi/helpers.js';
import { getDefaultControlSection, getInitialExpSectionName } from './helpers.js';
import { getSectionLabelNode } from './getSectionLabelNode.jsx';
import SectionMismatchWarning from './Components/SectionMismatchWarning.jsx';
import useControlSection from './hooks/useControlSection.js';

// MUI Imports
import { Button, Grid, Typography } from '@mui/material';
import SwitchInput from '../../../../../Components/SwitchInput/index.jsx';

const {
	CUSTOM_TARGETING: { KEY, CONTROL_VALUE, EXPERIMENT_VALUE }
} = AB_SPLIT_TEST_CONSTANTS;

const { KVP_VERSION_MAPPING_WITH_SPLITS } = MULTI_SPLIT_TEST_CONSTANTS;

const { CUSTOM_TARGETING_KEYDATA } = CONFIG_KEYDATA_CONSTANTS;

const CONTROL_SECTION_CONFIRMATION_MSG =
	'The currently live section must be used as the Control section. Please verify and check this box before proceeding.';

/**
 * @name AddNewExperimentModalContent
 * @description This component is the modal content to add a new\
 * experiment to the experiments list of expb config.\
 * This is triggered when user clicks on the + icon to create new experiment.
 */
const AddNewExperimentModalContent = forwardRef(({ addNewExperiment }) => {
	const commonStyleClasses = useCommonStyles();
	const { units, sectionLevelSplit } = useContext(UnitsContext);
	const { user } = useContext(UserContext);

	const [experimentName, setExperimentName] = useState('');
	const detectedLiveSectionRef = getDefaultControlSection(sectionLevelSplit, units);
	const [controlsectionRef, setControlsectionRef] = useState(detectedLiveSectionRef);
	const [expSectionName, setExpSectionName] = useState(getInitialExpSectionName(detectedLiveSectionRef, units));
	
	const { showDefaultControlSectionMismatchWarning } = useControlSection({
		units,
		controlsectionRef,
		detectedLiveSectionRef,
		setExpSectionName
	});

	const [abSplitKeyValueVersion, setAbSplitKeyValueVersion] = useState('');
	const [multiSplitEnabled, setMultiSplitEnabled] = useState(false);
	const [numberOfSplits, setNumberOfSplits] = useState(1);
	const [isLiveSectionConfirmed, setIsLiveSectionConfirmed] = useState(false);

	// Get the eligible ab split key value versions
	const eligibleAbSplitKeyValues = useMemo(() => {
		const availableKvpVersions = new Set([1, 2, 3, 4, 5]);
		const customTargetingKvpKeyData = CUSTOM_TARGETING_KEYDATA + '.targeting';
		// Iterates and checks if KVP version is in availableKvpVersions, if yes then it deletes the version from set.
		Object.keys(units).forEach((sectionRef) => {
			const targetingvalue = units[sectionRef][customTargetingKvpKeyData];
			if (targetingvalue && targetingvalue[KEY]) {
				const targetingValue = units[sectionRef][customTargetingKvpKeyData][KEY];
				const strTargetingVersion = targetingValue.match(/\d+$/);
				const version = parseInt(strTargetingVersion[0]);
				if (availableKvpVersions.has(version)) {
					availableKvpVersions.delete(version);
				}
			}
		});
		return availableKvpVersions;
	}, [units]);

	// Automatically sets the available ab split key value version.
	useEffect(() => {
		const eligibleAbSplitKeyValueList = Array.from(eligibleAbSplitKeyValues);
		// If No Key-Value Versions are Available then we return an error.
		if (eligibleAbSplitKeyValueList.length === 0) {
			toast.error('No Key-Value Pairs are Available for A/B Split Test.', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}
		setAbSplitKeyValueVersion(eligibleAbSplitKeyValueList[0]);
	}, [eligibleAbSplitKeyValues]);

	/**
	 * @name getEligibleAbSplitKeyValueList
	 * @description This function takes the eligible ab split key value versions
	 * and returns an array of {label, value} object.
	 * @param {Set} eligibleAbSplitKeyValues - Set of eligible ab split key value versions.
	 * @returns {object} - Array of {label, value} object of eligible ab split key value versions.
	 */
	const getEligibleAbSplitKeyValueList = useCallback(
		(eligibleAbSplitKeyValues) => {
			const eligibleAbSplitKeyValueList = [];
			eligibleAbSplitKeyValues.forEach((kvpVersion) => {
				const controlPart = `${CONTROL_VALUE}_${kvpVersion}`;
				const experimentPart = multiSplitEnabled
					? `${EXPERIMENT_VALUE}_${KVP_VERSION_MAPPING_WITH_SPLITS[numberOfSplits]}_${kvpVersion}`
					: `${EXPERIMENT_VALUE}_${kvpVersion}`;

				const kvpLabelAndValueObj = {
					label: `${KEY}: (${controlPart}) (${experimentPart})`,
					value: kvpVersion
				};
				eligibleAbSplitKeyValueList.push(kvpLabelAndValueObj);
			});
			return eligibleAbSplitKeyValueList;
		},
		[multiSplitEnabled, numberOfSplits]
	);

	// Array of label and Value Object of eligible ab split key value versions
	const eligibleAbSplitKeyValueList =
		eligibleAbSplitKeyValues && getEligibleAbSplitKeyValueList(eligibleAbSplitKeyValues);

	// Getting the eligible sections Array of label and value object
	// for A/B Split Test based on whether they have abSplitTestData or not.
	const eligibleSectionsForAbSplit = useMemo(() => {
		const eligibleSectionsArr = [];
		Object.keys(units).forEach((sectionRef) => {
			if (units[sectionRef].abSplitTestData || units[sectionRef].multiSplitTestData) {
				return;
			}
			const splitInfo = getExperimentSplitInfoOfSection(
				sectionLevelSplit,
				units[sectionRef],
				units
			);
			const sectionLabelAndValueObj = {
				label: getSectionLabelNode(units[sectionRef], splitInfo),
				value: sectionRef
			};
			eligibleSectionsArr.push(sectionLabelAndValueObj);
		});

		return eligibleSectionsArr;
	}, [units, sectionLevelSplit]);

	/**
	 * @function generateNumberOfSplitsOptions
	 * @description Generates an array of objects with label-value pairs.
	 * @param {number} size - The number of elements in the array.
	 * @returns {Array<{label: string, value: number}>} An array of objects with label and value properties.
	 */
	function generateNumberOfSplitsOptions(size) {
		let result = [];

		for (let i = 1; i <= size; i++) {
			result.push({
				label: String(i),
				value: i
			});
		}

		return result;
	}

	const startedAt = new Date().toISOString();

	return (
		<Grid
			container
			spacing={2}
			className={commonStyleClasses.modalContentStyle}
			width="fit-content !important"
			height="fit-content !important"
		>
			<Grid item xs={12} style={{ display: 'flex', flexDirection: 'column' }}>
				<SwitchInput
					isObjectEditor={false}
					directValue={multiSplitEnabled}
					updateDirectValue={(newVal) => setMultiSplitEnabled(newVal)}
					label="Enable Multi Split"
					disabled={sectionLevelSplit.splitLevel === SPLIT_LEVELS.SITE_LEVEL_SPLIT}
					containerStyles={{ marginLeft: '1rem', width: '50%' }}
				/>
				<Typography
					variant="body2"
					color="textSecondary"
					style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: '1rem' }}
				>
					(This feature is available only when the split level is set to Section Level Split)
				</Typography>
			</Grid>

			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<InputConfig
					label="Experiment Name"
					inputStyles={{ width: '100%', margin: '1rem 0' }}
					isObjectEditor={false}
					directValue={experimentName}
					updateDirectValue={setExperimentName}
				/>
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<InputConfig
					label="Exp Owner Email"
					inputStyles={{ width: '100%', margin: '1rem 0' }}
					isObjectEditor={false}
					directValue={user.email}
					editable={false}
				/>
			</Grid>
			<Grid
				item
				xs={6}
				className={commonStyleClasses.modalContentItem}
				style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
			>
				<DropDownSingle
					isObjectEditor={false}
					directValue={controlsectionRef}
					updateDirectValue={(value) => {
						setControlsectionRef(value);
						setIsLiveSectionConfirmed(false);
					}}
					list={eligibleSectionsForAbSplit}
					label="Control Video Section"
					formControlStyles={{ width: '100%' }}
					formControlVariant={'outlined'}
				/>
				{showDefaultControlSectionMismatchWarning && (
					<SectionMismatchWarning
						detectedSectionName={units[detectedLiveSectionRef]?.name || detectedLiveSectionRef}
					/>
				)}
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<InputConfig
					label="Exp Section Name"
					inputStyles={{ width: '100%', margin: '1rem 0' }}
					containerStyles={{ marginTop: '0.8rem' }}
					isObjectEditor={false}
					directValue={expSectionName}
					updateDirectValue={setExpSectionName}
					editable={false}
				/>
			</Grid>

			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<InputConfig
					label="Split Level"
					inputStyles={{ width: '100%', margin: '1rem 0' }}
					containerStyles={{ marginTop: '1rem' }}
					isObjectEditor={false}
					editable={false}
					directValue={sectionLevelSplit?.splitLevel}
				/>
			</Grid>

			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<InputConfig
					label="Started Date"
					inputStyles={{ width: '100%', margin: '1rem 0' }}
					containerStyles={{ marginTop: '1rem' }}
					isObjectEditor={false}
					editable={false}
					directValue={new Date(startedAt).toLocaleString()}
				/>
			</Grid>

			{multiSplitEnabled && (
				<Grid
					item
					xs={6}
					className={commonStyleClasses.modalContentItem}
					style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
				>
					<DropDownSingle
						isObjectEditor={false}
						directValue={numberOfSplits}
						updateDirectValue={(value) => {
							setNumberOfSplits(value);
						}}
						list={generateNumberOfSplitsOptions(4)}
						label="Number Of Splits"
						formControlStyles={{ width: '100%' }}
						formControlVariant={'outlined'}
						labelStyles={{ width: '70%' }}
					/>
				</Grid>
			)}

			<Grid
				item
				xs={12}
				className={commonStyleClasses.modalContentItem}
				style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
			>
				<DropDownSingle
					isObjectEditor={false}
					directValue={abSplitKeyValueVersion}
					updateDirectValue={(value) => {
						setAbSplitKeyValueVersion(value);
					}}
					list={eligibleAbSplitKeyValueList}
					label="Custom Key Value"
					formControlStyles={{ width: '100%' }}
					formControlVariant={'outlined'}
					labelStyles={{ width: '85%', textAlign: 'left' }}
					selectInputStyles={{ margin: '1rem 0' }}
				/>
			</Grid>

			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<CheckBoxInput
					directValue={isLiveSectionConfirmed}
					updateDirectValue={setIsLiveSectionConfirmed}
					label={CONTROL_SECTION_CONFIRMATION_MSG}
					isObjectEditor={false}
				/>
			</Grid>

			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<Button
					variant="contained"
					onClick={() => {
						if (!isLiveSectionConfirmed) {
							toast.error(CONTROL_SECTION_CONFIRMATION_MSG, {
								position: toast.POSITION.BOTTOM_RIGHT
							});
							return;
						}
						addNewExperiment(
							experimentName,
							user.email,
							controlsectionRef,
							expSectionName,
							abSplitKeyValueVersion,
							multiSplitEnabled,
							numberOfSplits,
							startedAt
						);
					}}
				>
					Add Experiment
				</Button>
			</Grid>
		</Grid>
	);
});

export default AddNewExperimentModalContent;
